import { useCallback, useEffect, useState, type ReactElement } from 'react'
import { Cloche } from '../components/Cloche'
import { useAuth, peutEcrire } from '../lib/auth'
import { EtatSync } from '../components/EtatSync'
import {
  IconeDevis,
  IconeInfo,
  IconePhotos,
  IconePlans,
  IconeSuivis,
  LogoComplet
} from '../components/Icones'
import type { Chantier as TChantier } from '../lib/types'
import { Photos } from './Photos'
import { Plans, type CiblePlan } from './Plans'
import { Devis } from './Devis'
import { Info } from './Info'
import { Suivis } from './Suivis'
import { useHistorique } from '../lib/historique'

type Onglet = 'photos' | 'plans' | 'devis' | 'suivis' | 'info'

const ONGLETS: { cle: Onglet; libelle: string; Icone: () => ReactElement }[] = [
  { cle: 'info', libelle: 'Info', Icone: IconeInfo },
  { cle: 'photos', libelle: 'Photos', Icone: IconePhotos },
  { cle: 'plans', libelle: 'Plans', Icone: IconePlans },
  { cle: 'devis', libelle: 'Devis', Icone: IconeDevis },
  { cle: 'suivis', libelle: 'Suivis', Icone: IconeSuivis }
]

export function Chantier({
  chantier,
  fermer,
  recharger,
  suiviCible,
  suiviCibleTraite,
  aller
}: {
  chantier: TChantier
  fermer: () => void
  recharger: () => void
  /** Fiche de suivi à ouvrir (avis, lien du courriel). */
  suiviCible?: string | null
  suiviCibleTraite?: () => void
  /** Ouvrir un autre chantier / suivi depuis la cloche. */
  aller: (chantierId: string, suiviId: string | null) => void
}) {
  const { profil } = useAuth()
  const [onglet, setOnglet] = useState<Onglet>('info')
  const [camera, setCamera] = useState(false)
  useHistorique(onglet, setOnglet)
  useHistorique(camera, setCamera)
  const [depuisPhoto, setDepuisPhoto] = useState<string | null>(null)
  const depuisPhotoTraitee = useCallback(() => setDepuisPhoto(null), [])
  const [ciblePlan, setCiblePlan] = useState<CiblePlan | null>(null)
  const cibleTraitee = useCallback(() => setCiblePlan(null), [])
  const ecrivable = peutEcrire(profil, chantier.statut)

  // Avis : passer à l'onglet Suivis, qui ouvrira la fiche.
  useEffect(() => {
    if (suiviCible) setOnglet('suivis')
  }, [suiviCible])

  // Bouton de création contextuel (décision d'ergonomie no 3) : il n'existe
  // que là où l'onglet répond déjà à la question « créer quoi ».
  const fabVisible = ecrivable && onglet === 'photos'

  return (
    <div className="app">
      <header className="entete">
        <div className="logo-centre petit">
          <LogoComplet hauteur={30} />
        </div>
        <div className="entete-rang">
          <button className="retour" onClick={fermer}>
            ‹ Chantiers
          </button>
          <div style={{ flex: 1, minWidth: 0 }}>
            <h1 style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {chantier.nom}
            </h1>
            <p className="sous">
              {chantier.numero}
              {chantier.adresse ? ` · ${chantier.adresse}` : ''}
            </p>
          </div>
          <EtatSync />
          <Cloche aller={aller} />
        </div>
      </header>

      <div className="corps">
        {chantier.statut === 'termine' && (
          <p className="avis" style={{ marginBottom: 14 }}>
            Chantier archivé — lecture seule. Rien ne peut plus y être ajouté ni modifié.
          </p>
        )}

        {onglet === 'photos' && (
          <Photos
            chantier={chantier}
            ouvrirCamera={camera}
            setOuvrirCamera={setCamera}
            creerDeficience={
              ecrivable
                ? (uuid) => {
                    setDepuisPhoto(uuid)
                    setOnglet('suivis')
                  }
                : undefined
            }
          />
        )}
        {onglet === 'plans' && <Plans chantier={chantier} cible={ciblePlan} cibleTraitee={cibleTraitee} />}
        {onglet === 'devis' && <Devis chantier={chantier} />}
        {onglet === 'suivis' && (
          <Suivis
            chantier={chantier}
            depuisPhoto={depuisPhoto}
            depuisPhotoTraitee={depuisPhotoTraitee}
            suiviCible={suiviCible}
            suiviCibleTraite={suiviCibleTraite}
            voirSurPlan={(s) => {
              if (!s.epingle_document_id || s.epingle_page == null) return
              setCiblePlan({
                documentId: s.epingle_document_id,
                page: s.epingle_page,
                x: s.epingle_x ?? 0.5,
                y: s.epingle_y ?? 0.5,
                suiviId: s.id
              })
              setOnglet('plans')
            }}
          />
        )}
        {onglet === 'info' && <Info chantier={chantier} recharger={recharger} />}
      </div>

      {fabVisible && (
        <button className="fab" onClick={() => setCamera(true)} aria-label="Prendre une photo">
          +
        </button>
      )}

      <nav className="onglets" role="tablist" aria-label="Sections du chantier">
        {ONGLETS.map(({ cle, libelle, Icone }) => (
          <button
            key={cle}
            role="tab"
            aria-selected={onglet === cle}
            onClick={() => setOnglet(cle)}
          >
            <Icone />
            {libelle}
            <span className="pip" />
          </button>
        ))}
      </nav>
    </div>
  )
}
